<template>
  <div class="dropdown" :class="[{ 'd-block': isVisible }]">
    <button
      :id="dropdownId"
      ref="buttonEl"
      :type="type"
      :class="buttonClasses"
      :disabled="disabled"
      data-bs-toggle="dropdown"
      :data-bs-auto-close="autoClose"
      aria-expanded="false"
      data-testid="bs-dropdown-toggle"
      @click="handleButtonClick"
    >
      <slot name="button-content">{{ text }}</slot>
    </button>

    <ul
      ref="menuEl"
      class="dropdown-menu"
      :class="menuClasses"
      :aria-labelledby="dropdownId"
      data-testid="bs-dropdown-menu"
    >
      <slot />
    </ul>
  </div>
</template>

<script>
import DropdownPkg from 'bootstrap/js/dist/dropdown';

const Dropdown = DropdownPkg.default || DropdownPkg;

export default {
  name: 'BsDropdown',
  provide() {
    return {
      dropdownInstance: this,
    };
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    text: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'secondary',
    },
    size: {
      type: String,
      default: null,
    },
    split: {
      type: Boolean,
      default: false,
    },
    splitButtonType: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value),
    },
    splitVariant: {
      type: String,
      default: null,
    },
    splitHref: {
      type: String,
      default: '#',
    },
    splitTo: {
      type: [String, Object],
      default: null,
    },
    noCaret: {
      type: Boolean,
      default: false,
    },
    toggleClass: {
      type: [String, Array, Object],
      default: null,
    },
    menuClass: {
      type: [String, Array, Object],
      default: null,
    },
    dropleft: {
      type: Boolean,
      default: false,
    },
    dropright: {
      type: Boolean,
      default: false,
    },
    dropup: {
      type: Boolean,
      default: false,
    },
    dropleft: {
      type: Boolean,
      default: false,
    },
    dropend: {
      type: Boolean,
      default: false,
    },
    dropstart: {
      type: Boolean,
      default: false,
    },
    autoClose: {
      type: [Boolean, String],
      default: 'outside',
    },
    boundary: {
      type: [String, HTMLElement],
      default: 'clippingParents',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    offset: {
      type: [String, Number, Array, Object],
      default: undefined,
    },
    type: {
      type: String,
      default: 'button',
    },
  },
  data() {
    return {
      bsDropdown: null,
      isVisible: false,
      pendingShow: false,
    };
  },
  computed: {
    dropdownId() {
      return this.id || `bs-dropdown-${this._uid}`;
    },
    hasButtonContentSlot() {
      return this.$scopedSlots['button-content'] !== undefined;
    },
    buttonClasses() {
      const classes = ['btn', `btn-${this.variant}`];

      if (this.size) {
        classes.push(`btn-${this.size}`);
      }

      // Only add caret classes when not using noCaret
      if (!this.noCaret) {
        classes.push('dropdown-toggle');
      } else {
        classes.push('dropdown-toggle-split');
      }

      if (this.toggleClass) {
        classes.push(this.toggleClass);
      }

      return classes;
    },
    menuClasses() {
      const classes = [];

      if (this.menuClass) {
        classes.push(this.menuClass);
      }

      if (this.dropup) {
        classes.push('dropup');
      }
      if (this.dropright) {
        classes.push('dropright');
      }
      if (this.dropleft) {
        classes.push('dropleft');
      }
      if (this.dropend) {
        classes.push('dropend');
      }
      if (this.dropstart) {
        classes.push('dropstart');
      }

      return classes;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.bindDropdown();
      if (this.pendingShow) {
        this.pendingShow = false;
        if (this.bsDropdown) {
          this.bsDropdown.show();
        }
      }
    });
  },
  beforeDestroy() {
    this.pendingShow = false;
    this.unbindDropdown();
  },
  methods: {
    bindDropdown() {
      const el = this.$refs.buttonEl;
      if (!el || typeof Dropdown.getOrCreateInstance !== 'function') return;

      const config = {
        autoClose: this.autoClose,
        boundary: this.boundary,
      };
      // Only pass offset if explicitly provided (not null/undefined)
      if (this.offset != null) {
        config.offset = this.offset;
      }

      this.bsDropdown = Dropdown.getOrCreateInstance(el, config);

      el.addEventListener('shown.bs.dropdown', this.onShown);
      el.addEventListener('hidden.bs.dropdown', this.onHidden);
    },
    unbindDropdown() {
      const el = this.$refs.buttonEl;
      if (!el) return;

      el.removeEventListener('shown.bs.dropdown', this.onShown);
      el.removeEventListener('hidden.bs.dropdown', this.onHidden);

      if (this.bsDropdown) {
        this.bsDropdown.dispose();
        this.bsDropdown = null;
      }
    },
    handleButtonClick(event) {
      if (this.disabled) {
        event.preventDefault();
        return;
      }
      this.$emit('click', event);
    },
    onShown() {
      this.isVisible = true;
      this.$emit('shown');
    },
    onHidden() {
      this.isVisible = false;
      this.$emit('hidden');
    },
    show() {
      if (this.bsDropdown) {
        this.bsDropdown.show();
        return;
      }
      this.pendingShow = true;
    },
    hide() {
      if (this.bsDropdown) {
        this.bsDropdown.hide();
      }
    },
    toggle() {
      if (this.bsDropdown) {
        this.bsDropdown.toggle();
      }
    },
  },
};
</script>

<style scoped>
</style>